import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

type LocationState = {
  amount?: number | null;
  currency?: string | null;
  packageName?: string | null;
  packageId?: string | null;
  adId?: string | null;
  referenceId?: string | null;
  createdAt?: string | Date | null;
  from?: string | null;
};

const PaymentConfirmation: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const state = (location.state || {}) as LocationState;
  const amount = typeof state.amount === 'number' ? state.amount : null;
  const currency = state.currency || 'USD';
  const packageName = state.packageName || null;
  const referenceId = state.referenceId || null;
  const createdAtRaw = state.createdAt || null;
  const createdAtDate = createdAtRaw ? new Date(createdAtRaw) : null;
  const fromPath = state.from || null;
  const fromCampaignWizard = typeof fromPath === 'string' && fromPath.startsWith('/campaign');

  const handleBackToPayment = () => {
    navigate('/payment');
  };

  const isMissingContext = !amount && !referenceId && !createdAtRaw;

  return (
    <main className="bg-slate-50 dark:bg-slate-900">
      <div className="mx-auto max-w-2xl px-4 py-8 space-y-6">
        <header className="space-y-1">
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 sm:text-3xl">
            Payment confirmation
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            {isMissingContext
              ? 'We could not find a recent payment to display. Use the links below to review your payments or start a new campaign.'
              : fromCampaignWizard
                ? 'Your campaign funding has been processed. A summary of this event is below.'
                : 'Your payment has been processed. A summary of this funding event is below.'}
          </p>
        </header>

        {!isMissingContext && (
          <section className="rounded-xl border border-emerald-200 bg-white dark:bg-slate-900 p-4 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 h-6 w-6 flex-shrink-0 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-semibold">
              ✓
            </div>
            <div className="space-y-1">
              <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Payment successful</h2>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {amount !== null ? (
                  <>
                    We received
                    <span className="font-semibold"> {currency} {amount.toFixed(2)}</span>
                    {packageName ? (
                      <>
                        {' '}
                        towards
                        <span className="font-semibold"> {packageName}</span>.
                      </>
                    ) : (
                      ' towards your next TELLURVISION campaign.'
                    )}
                  </>
                ) : (
                  'Your payment has been recorded for this campaign.'
                )}
              </p>
              <div className="mt-2 space-y-0.5 text-xs text-slate-500 dark:text-slate-400">
                {referenceId && (
                  <p>
                    <span className="font-medium text-slate-700 dark:text-slate-300">Reference ID:</span>{' '}
                    <span className="font-mono text-[11px]">{referenceId}</span>
                  </p>
                )}
                {createdAtDate && !Number.isNaN(createdAtDate.getTime()) && (
                  <p>
                    <span className="font-medium text-slate-700 dark:text-slate-300">Time:</span>{' '}
                    {createdAtDate.toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          </div>
        </section>
        )}

        <section className="space-y-3 text-sm text-slate-700 dark:text-slate-300">
          <p>
            You can review this and other activity any time from the
            <button
              type="button"
              onClick={handleBackToPayment}
              className="ml-1 inline-flex items-center text-blue-600 hover:text-blue-700 hover:underline"
            >
              Payments
            </button>
            page.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleBackToPayment}
              className="inline-flex items-center rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-300 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              View payments
            </button>
            <Link
              to="/campaigns"
              className="inline-flex items-center rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-300 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              View my campaigns
            </Link>
            <Link
              to="/campaign/start"
              className="inline-flex items-center rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Start another campaign
            </Link>
            <Link
              to="/"
              className="inline-flex items-center rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-300 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Go home
            </Link>
          </div>
        </section>
      </div>
    </main>
  );
};

export default PaymentConfirmation;
